import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AuthContext, roleCan } from "./auth.js";
import { authService } from "@/lib/api.js";
import {
    clearSession,
    readSession,
    registerUnauthorizedHandler,
    writeSession,
} from "@/lib/apiClient.js";

/*
 * Replaces the localStorage reads scattered across thirteen files in the Next.js
 * app, and the unused NextAuth setup alongside them.
 *
 * ONE redirect authority: this provider never navigates. It records *why* the
 * session ended and RequireAuth decides where to send the user.
 *
 * That is not a style choice. Navigating from here while also clearing state
 * meant two redirects raced — the guard's plain "/login" landed first and the
 * interceptor's "/login?error=session-expired" was lost, so an expired session
 * looked like an ordinary sign-out with no explanation.
 */
export function AuthProvider({ children }) {
    const queryClient = useQueryClient();

    const [session, setSession] = useState(() => readSession());

    // Why the session ended: null (never had one) | "session-expired" | "signed-out".
    // RequireAuth turns this into the query string on /login.
    const [endedReason, setEndedReason] = useState(null);

    // Until the stored token has been checked once we know we have a token but not
    // whether it still works. Pages should wait rather than fire doomed requests.
    const [verifying, setVerifying] = useState(() => Boolean(readSession()));
    const verifiedRef = useRef(false);

    const endSession = useCallback(
        (reason) => {
            clearSession();
            verifiedRef.current = false;
            setVerifying(false);
            setSession(null);
            setEndedReason(reason);
            // A previous user's cached queries must not survive into the next session.
            queryClient.clear();
        },
        [queryClient],
    );

    /* ---- Sign in ---------------------------------------------------------- */
    const login = useCallback(
        async (credentials) => {
            const response = await authService.login(credentials);
            const payload = response?.data?.data;

            if (response?.data?.status !== "success" || !payload?.token) {
                // A 2xx without a token means the contract changed; failing loudly
                // beats storing an empty session and 401-ing on every request.
                throw new Error("Unexpected response from the server. Please try again.");
            }

            const next = { user: payload.user, accessToken: payload.token };
            writeSession(next);
            verifiedRef.current = true;
            setVerifying(false);
            setEndedReason(null);
            setSession(next);
            queryClient.clear();

            return payload.user;
        },
        [queryClient],
    );

    /* ---- Sign out --------------------------------------------------------- */
    const logout = useCallback(() => endSession("signed-out"), [endSession]);

    /*
     * Re-read the signed-in user.
     *
     * The stored session is also what the sidebar and topbar render, so a page
     * that changes the user's own record — the profile form renaming them —
     * has to say so here, or the heading updates and the chrome keeps the old
     * name until the next reload.
     *
     * Failures are deliberately swallowed: this is a display refresh, and a
     * blip on it must not disturb a session that is otherwise fine. A 401 is
     * already handled by the interceptor.
     */
    const refresh = useCallback(async () => {
        try {
            const res = await authService.getProfile();
            const fresh = res?.data?.data?.user;
            if (!fresh) return null;

            setSession((prev) => {
                if (!prev) return prev;
                const next = { ...prev, user: { ...prev.user, ...fresh } };
                writeSession(next);
                return next;
            });
            return fresh;
        } catch {
            return null;
        }
    }, []);

    /* ---- A 401 anywhere ends the session ---------------------------------- */
    useEffect(() => {
        return registerUnauthorizedHandler(() => endSession("session-expired"));
    }, [endSession]);

    /* ---- Verify a stored token once on boot -------------------------------
     * Without this a stale token renders the whole shell and then every request
     * fails one by one. One /auth/profile call decides it up front, and refreshes
     * the user record in case a role or name changed elsewhere.
     *
     * No `cancelled` flag here, deliberately. Pairing one with verifiedRef
     * deadlocked under StrictMode: mount fired the request, cleanup marked it
     * cancelled, the second mount early-returned on verifiedRef so nothing
     * re-fired, and the single in-flight response was then discarded — verifying
     * never went false and the app sat on "Checking your session…" forever.
     * verifiedRef alone already guarantees exactly one request per token, and a
     * late setState after unmount is a harmless no-op in React 18+.
     * -------------------------------------------------------------------- */
    useEffect(() => {
        if (!session?.accessToken || verifiedRef.current) return;

        verifiedRef.current = true;

        authService
            .getProfile()
            .then((res) => {
                const fresh = res?.data?.data?.user;
                if (!fresh) return;
                setSession((prev) => {
                    if (!prev) return prev;
                    const next = { ...prev, user: { ...prev.user, ...fresh } };
                    writeSession(next);
                    return next;
                });
            })
            .catch((error) => {
                // A 401 is already handled by the interceptor. Anything else — the
                // API down, a network blip — must NOT sign the user out; let them
                // keep the session and see the real error per page.
                if (import.meta.env.DEV && error?.response?.status !== 401) {
                    console.warn("Profile check failed:", error?.message);
                }
            })
            .finally(() => setVerifying(false));

        // Keyed on the token: runs once per token, and verifiedRef guards the
        // double invocation StrictMode does in development.
    }, [session?.accessToken]);

    const value = useMemo(() => {
        const user = session?.user ?? null;
        const role = user?.role ?? null;

        return {
            user,
            role,
            token: session?.accessToken ?? null,
            isAuthenticated: Boolean(session?.accessToken),
            verifying,
            endedReason,
            login,
            logout,
            refresh,
            can: (capability) => roleCan(role, capability),
            hasRole: (...roles) => roles.includes(role),
        };
    }, [session, verifying, endedReason, login, logout, refresh]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
